import { EmptyView, emptyViewVariants } from '@components'

import { Button, ModuleLayout } from '@billy/design-system'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router'

import { EInvoicingSetupContextProvider } from '@features/eInvoicing/context/EInvoicingSetupContext'
import { EInvoicingDashboardBanner } from '@features/eInvoicing/elements/EinvoicingDashboardBanner'

import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useBillsListFilters } from '../BillsList/legacy'
import { useIsBillsListEmpty } from '../BillsList/legacy/hooks/useIsBillsListEmpty'

export const BillsListEmpty = (): ReactElement | null => {
  const { t } = useTranslation()
  const [, setFilters] = useBillsListFilters()
  const isBillsListEmpty = useIsBillsListEmpty()
  const navigate = useNavigate()
  const { url: billsNewUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getNewBillRoute())
  const { url: billsSettingsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getSettingsRoute())
  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())

  // Clear filters in case they left after redirecting from `BillsList`
  useEffect(() => {
    setFilters({
      page: undefined,
      pageSize: undefined,
      period: undefined,
      searchQuery: undefined,
      sortDirection: undefined,
      sortProperty: undefined,
      status: undefined,
    })
  }, [setFilters])

  const { animation, description, title, buttonText } = emptyViewVariants.bills

  const handleEmptyViewClick = useCallback(() => {
    if (billsNewUrl) {
      navigate(billsNewUrl)
    }
  }, [billsNewUrl, navigate])

  if (!billsUrl) {
    return null
  }

  if (!isBillsListEmpty) {
    return <Navigate to={billsUrl} replace />
  }

  return (
    <ModuleLayout sideActions={<Button icon="settings" variant="outlined" to={billsSettingsUrl} />} title={t('bills')}>
      <EInvoicingSetupContextProvider>
        <EInvoicingDashboardBanner source="bills-list-empty" />
      </EInvoicingSetupContextProvider>
      <EmptyView
        animationName={animation}
        onClick={handleEmptyViewClick}
        showCTA={!!buttonText?.length}
        tButton={buttonText}
        tDescription={description}
        tTitle={title}
      />
    </ModuleLayout>
  )
}
